import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { catchError, map } from 'rxjs/operators';
import { IDeviceSetting } from './device-setting.interfaces';
import { ERROR_CODES } from 'src/app/shared/constants/error-codes';
import {
  NotFoundError,
  OperationFailedError,
} from 'src/app/shared/errors/errors';
import { LANGUAGE_CONSTANTS as LANG_CONST } from 'src/app/shared/constants/language-constants';
import { StateManagementService } from 'src/app/shared/services/state-management.service';
import { IDevice } from '../building-unit-details/add-lock/lock-management-interfaces';
import { IUser } from 'src/app/user-management/user-management-interfaces';
import { CONSTANTS } from 'src/app/shared/constants/constants';
import {
  deviceModeResponse,
  deviceOpenedStateResponse,
  deviceStatusResponse,
  userTypeResponse,
} from 'src/app/shared/constants/response-mapping';
import { ActivationStateRequest } from 'src/app/shared/constants/request-mapping';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class DeviceSettingsService {
  private url = environment.graphqlUrl;
  constructor(
    private http: HttpClient,
    private stateManagementService: StateManagementService,
    private translate: TranslateService
  ) {}

  getDeviceInfo(deviceId: string): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'query ($deviceId: String!) {  getDeviceInfo(deviceId: $deviceId) { deviceId deviceName deviceType modelNumber firmwareVersion hardwareVariant hardwareVersion appVersion model serialNumber macAddress lockVersion bluetoothVersion batteryPercentage buzzerEnabled autoLockTime deviceStatus autoLockEnabled updatedAt updatedTime activationDate manufacturingDate productType imageName } }',
        variables: {
          deviceId,
        },
      })
      .pipe(
        map((res: any) => {
          if (res?.data?.getDeviceInfo) {
            const deviceInfo: any = res.data;
            if (
              deviceStatusResponse.hasOwnProperty(
                deviceInfo?.getDeviceInfo?.deviceStatus
              )
            ) {
              deviceInfo.getDeviceInfo.deviceStatus =
                deviceStatusResponse[deviceInfo?.getDeviceInfo?.deviceStatus];
            }

            return deviceInfo;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.DEVICE_NOT_FOUND:
              throw new NotFoundError(
                this.translate.instant('DEVICE_NOT_FOUND')
              );
            case ERROR_CODES.FAILED:
              throw new OperationFailedError(
                this.translate.instant('REQUEST_TO_GET_DEVICE_INFO_FAILED')
              );
            default:
              throw new Error(
                this.translate.instant('REQUEST_TO_GET_DEVICE_INFO_FAILED')
              );
          }
        })
      );
  }

  /**
   * @description getUnit method returns all details related to units
   * @param { string } deviceId

   * @returns { Observable<any> }
   */
  getUsersByDevice(deviceId: string): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'query ($deviceId: String!) { getUsersByDevice (deviceId: $deviceId) { email firstName lastName faceId  invitationPending isMobileCredentialEnabled mFAEnabled phoneNumber userId  userImgKey userImgName userImgUrl userType } } ',
        variables: {
          deviceId,
        },
      })
      .pipe(
        map((res: any) => {
          if (res?.data?.getUsersByDevice) {
            return res?.data;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.USER_NOT_FOUND:
              throw new NotFoundError(this.translate.instant('USER_NOT_FOUND'));
            case ERROR_CODES.USER_NOT_AUTHORIZED:
              throw new NotFoundError(
                this.translate.instant('USER_NOT_AUTHORIZED')
              );
            case ERROR_CODES.FAILED:
              throw new OperationFailedError(
                this.translate.instant('REQUEST_TO_GET_USERS_BY_DEVICE_FAILED')
              );
            default:
              throw new Error(
                this.translate.instant('REQUEST_TO_GET_USERS_BY_DEVICE_FAILED')
              );
          }
        })
      );
  }

  /**
   * @description getPartnerDeviceInfo method returns all details related to partner device.
   * @param { string } deviceId
   * @returns { Observable<any> }
   */
  getPartnerDeviceInfo(deviceId: string): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'query ($deviceId: String!) { getPartnerDevice(deviceId: $deviceId) { deviceName, macAddress, deviceType, additionalData deviceState enabled deviceMode deviceOpenedState deviceKind }   }',
        variables: {
          deviceId,
        },
      })
      .pipe(
        map((res: any) => {
          if (res?.data?.getPartnerDevice) {
            let deviceInfo: any = res.data;
            //mapping device information
            if (
              deviceOpenedStateResponse.hasOwnProperty(
                deviceInfo?.getPartnerDevice.deviceOpenedState
              )
            ) {
              deviceInfo.getPartnerDevice.deviceOpenedState =
                deviceOpenedStateResponse[
                  deviceInfo.getPartnerDevice.deviceOpenedState
                ];
            } else {
              deviceInfo.getPartnerDevice.deviceOpenedState =
                deviceOpenedStateResponse[0];
            }
            return deviceInfo;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.DEVICE_NOT_FOUND:
              throw new NotFoundError(
                this.translate.instant('DEVICE_NOT_FOUND')
              );
            case ERROR_CODES.FAILED:
              throw new OperationFailedError(
                this.translate.instant('REQUEST_TO_GET_DEVICE_INFO_FAILED')
              );
            default:
              throw new Error(
                this.translate.instant('REQUEST_TO_GET_DEVICE_INFO_FAILED')
              );
          }
        })
      );
  }

  /**
   * @description getUsersByPartnerDevice method returns all details related to users against device
   * @param { string } deviceId
   * @returns { Observable<any> }
   */
  getUsersByPartnerDevice(accessPointId: string): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query: `query ($accessPointId: String!) {
          getUsersByAccessPoint (accessPointId: $ accessPointId) {
                 email
                  firstName
                  lastName
                  faceId
                  invitationPending
                  isMobileCredentialEnabled
                  mFAEnabled
                  phoneNumber
                  userId
                  userImgKey
                  userImgName
                  userImgUrl
                  userType        
  }
      }
  `,
        variables: {
          accessPointId,
        },
      })
      .pipe(
        map((res: any) => {
          if (res?.data?.getUsersByAccessPoint) {
            const usersList: IUser[] = res?.data?.getUsersByAccessPoint;
            this.stateManagementService.updateUnitOrAccessPointUsersList(
              usersList
            );
            return usersList;
          } else if (res?.errors?.length > 0) {
            this.stateManagementService.updateUnitOrAccessPointUsersList([]);
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          this.stateManagementService.updateUnitOrAccessPointUsersList([]);
          switch (err.message) {
            case ERROR_CODES.FAILED:
              throw new NotFoundError(
                this.translate.instant('REQUEST_TO_GET_USER_INFO_FAILED')
              );
            default:
              throw new Error(
                this.translate.instant('REQUEST_TO_GET_USER_INFO_FAILED')
              );
          }
        })
      );
  }

  /**
   * @description deletPartnerDevice method will delete the partner device.
   * @param { string } deviceId
   * @returns { Observable<any> }
   */
  deletPartnerDevice(deviceId: string): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'mutation ($deviceId: String!) { deletePartnerDevice(deviceId: $deviceId) { deviceId }   }',
        variables: {
          deviceId,
        },
      })
      .pipe(
        map((res: any) => {
          if (res?.data?.deletePartnerDevice) {
            const deletePartnerDevice =
              res?.data?.deletePartnerDevice?.deviceId;
            const devices = this.stateManagementService.getParttnerDeviceList();

            const updatedDevices = devices?.filter(
              (device) =>
                !(
                  deletePartnerDevice &&
                  deletePartnerDevice.includes(device.deviceId?.split('-')[0])
                )
            );

            this.stateManagementService.updatePartnerDeviceList(updatedDevices);
            return res?.data;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.DEVICE_NOT_FOUND:
              throw new NotFoundError(
                this.translate.instant('DEVICE_NOT_FOUND')
              );
            case ERROR_CODES.FAILED:
              throw new OperationFailedError(
                this.translate.instant('REQUEST_TO_DELETE_DEVICE_INFO_FAILED')
              );
            default:
              throw new Error(
                this.translate.instant('REQUEST_TO_DELETE_DEVICE_INFO_FAILED')
              );
          }
        })
      );
  }

  /**
   * @description deleteLockDevice method will delete the lock device.
   * @param { string } deviceId
   * @returns { Observable<any> }
   */
  deleteLockDevice(deviceId: string): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'mutation ($deviceId: String!) { deleteDevice(deviceId: $deviceId) { deviceId }}',
        variables: {
          deviceId,
        },
      })
      .pipe(
        map((res: any) => {
          if (res?.data?.deleteDevice) {
            let devices = this.stateManagementService.getDeviceList();
            // reading the device so that it's status can be checked.
            const index = devices?.findIndex(
              (device) => device?.deviceId === res?.data?.deleteDevice?.deviceId
            );
            let updatedDevice: any = devices.find(
              (device) => device?.deviceId === res?.data?.deleteDevice?.deviceId
            );

            if (updatedDevice.activationState === CONSTANTS.ACTIVATED) {
              updatedDevice.activationState = CONSTANTS.PENDING_DEACTIVATION;
              const index = devices.findIndex(
                (device) =>
                  device?.deviceId === res?.data?.deleteDevice?.deviceId
              );
              const updatedDevices = [...devices];
              if (index !== -1) {
                updatedDevices[index] = updatedDevice;
              }
              this.stateManagementService.updateDeviceList(updatedDevices);
            } else {
              //removing device with the index
              devices = devices.filter(
                (device) =>
                  device?.deviceId !== res?.data?.deleteDevice?.deviceId
              );
            }

            this.stateManagementService.updateDeviceList(devices);
            return res?.data;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.DEVICE_NOT_FOUND:
              throw new NotFoundError(
                this.translate.instant('DEVICE_NOT_FOUND')
              );
            case ERROR_CODES.FAILED:
              throw new OperationFailedError(
                this.translate.instant('REQUEST_TO_DELETE_DEVICE_INFO_FAILED')
              );
            default:
              throw new Error(
                this.translate.instant('REQUEST_TO_DELETE_DEVICE_INFO_FAILED')
              );
          }
        })
      );
  }

  /**
   * @description deletPartnerDevice method will delete the partner device.
   * @param { string } deviceId
   * @returns { Observable<any> }
   */
  updatePartnerDeviceType(
    deviceId: string,
    deviceType: string
  ): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'mutation ($deviceId: String!, $deviceType: String!) { updatePartnerDevice(deviceId: $deviceId, deviceType: $deviceType) { deviceId }}',
        variables: {
          deviceId,
          deviceType,
        },
      })
      .pipe(
        map((res: any) => {
          if (res?.data?.updatePartnerDevice) {
            const devices = this.stateManagementService.getParttnerDeviceList();
            devices?.forEach((updatedDevice) => {
              if (updatedDevice?.deviceId === deviceId) {
                updatedDevice.deviceType = deviceType;
              }
            });
            this.stateManagementService.updatePartnerDeviceList(devices);
            return res?.data;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.DEVICE_NOT_FOUND:
              throw new NotFoundError(
                this.translate.instant('DEVICE_NOT_FOUND')
              );
            case ERROR_CODES.FAILED:
              throw new OperationFailedError(
                this.translate.instant('REQUEST_TO_UPDATE_DEVICE_TYPE_FAILED')
              );
            default:
              throw new Error(
                this.translate.instant('REQUEST_TO_UPDATE_DEVICE_TYPE_FAILED')
              );
          }
        })
      );
  }

  /**
   * @description updateDeviceMode method will update the partner device.
   * @param { string } deviceId
   * @param { string } deviceMode
   * @returns { Observable<any> }
   */
  updateDeviceMode(deviceId: string, deviceMode: number): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'mutation ($deviceId: String! $deviceMode: Int!) {updateDeviceMode (deviceId: $deviceId, deviceMode: $deviceMode) {deviceId}} ',
        variables: {
          deviceId,
          deviceMode,
        },
      })
      .pipe(
        map((res: any) => {
          if (res?.data?.updateDeviceMode) {
            const devices = this.stateManagementService.getParttnerDeviceList();
            devices?.forEach((updatedDevice) => {
              if (updatedDevice?.deviceId === deviceId) {
                updatedDevice.deviceMode = deviceModeResponse[deviceMode];
              }
            });
            this.stateManagementService.updatePartnerDeviceList(devices);
            return res?.data;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.DEVICE_NOT_FOUND:
              throw new NotFoundError(
                this.translate.instant('DEVICE_NOT_FOUND')
              );
            case ERROR_CODES.FAILED:
              throw new OperationFailedError(
                this.translate.instant('REQUEST_TO_UPDATE_DEVICE_TYPE_FAILED')
              );
            case ERROR_CODES.UNABLE_TO_UPDATE_DEVICE_ON_PARTNER:
              throw new Error(
                this.translate.instant('UNABLE_TO_UPDATE_DEVICE_ON_PARTNER')
              );
            default:
              throw new Error(
                this.translate.instant('REQUEST_TO_UPDATE_DEVICE_TYPE_FAILED')
              );
          }
        })
      );
  }

  /**
   * @description updateDeviceEnablingState method will update the device enable state.
   * @param { string } deviceId
   * @param { boolean } enabled
   * @returns { Observable<any> }
   */
  updateDeviceEnablingState(
    deviceId: string,
    enabled: boolean
  ): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'mutation ($deviceId: String! $enabled: String!) {updateEnablingState (deviceId: $deviceId, enabled: $enabled) {deviceId}}',
        variables: {
          deviceId,
          enabled,
        },
      })
      .pipe(
        map((res: any) => {
          if (res?.data?.updateEnablingState) {
            const devices = this.stateManagementService.getParttnerDeviceList();
            devices?.forEach((updatedDevice) => {
              if (updatedDevice?.deviceId === deviceId) {
                updatedDevice.enabled = enabled;
              }
            });
            this.stateManagementService.updatePartnerDeviceList(devices);
            return res?.data;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.DEVICE_NOT_FOUND:
              throw new NotFoundError(
                this.translate.instant('DEVICE_NOT_FOUND')
              );
            case ERROR_CODES.FAILED:
              throw new OperationFailedError(
                this.translate.instant('REQUEST_TO_UPDATE_DEVICE_TYPE_FAILED')
              );
            default:
              throw new Error(
                this.translate.instant('REQUEST_TO_UPDATE_DEVICE_TYPE_FAILED')
              );
          }
        })
      );
  }

  /**
   * @description updateDeviceName method will update the lock name.
   * @param { string } deviceId
   * @param { string } deviceName
   * @returns { Observable<any> }
   */
  updateDeviceName(deviceId: string, deviceName: string): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'mutation ($deviceId: String!, $deviceName: String) { updateDeviceSettings(deviceId: $deviceId, deviceName: $deviceName) { deviceId }}',
        variables: {
          deviceId,
          deviceName,
        },
      })
      .pipe(
        map((res: any) => {
          if (res?.data?.updateDeviceSettings) {
            const devices = this.stateManagementService.getDeviceList();
            devices?.forEach((updatedDevice) => {
              if (updatedDevice?.deviceId === deviceId) {
                updatedDevice.deviceName = deviceName;
              }
            });
            this.stateManagementService.updateDeviceList(devices);
            return res?.data;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.DEVICE_NAME_ALREADY_EXISTS:
              throw new NotFoundError(
                this.translate.instant('LOCK_NAME_ALREADY_EXIST')
              );
            case ERROR_CODES.DEVICE_NOT_FOUND:
              throw new NotFoundError(
                this.translate.instant('DEVICE_NOT_FOUND')
              );
            case ERROR_CODES.DEVICE_ID_ALREADY_EXISTS:
              throw new OperationFailedError(
                this.translate.instant('DEVICE_ID_ALREADY_EXISTS')
              );
            case ERROR_CODES.FAILED:
              throw new OperationFailedError(
                this.translate.instant('REQUEST_TO_UPDATE_DEVICE_NAME_FAILED')
              );
            default:
              throw new Error(
                this.translate.instant('REQUEST_TO_UPDATE_DEVICE_NAME_FAILED')
              );
          }
        })
      );
  }
}
